import {
    Component,
    forceUpdate,
    Host,
    h,
    Element,
    Prop,
    State,
    ComponentInterface
} from "@stencil/core";
import { ShopLink, ShopType } from "../my-machines-response";
import { ButtonSize } from "@trumpf/ux.ui/dist/types/components";
import { defineCustomElement as defineAuthImage } from "@trumpf/ux.application-framework/dist/components/uxc-auth-image";
import { ServiceCaseCreateConfig } from "../../service-case-create/service-case-create-config";
import { ServiceCaseCreateModal } from "../../service-case-create/service-case-create-modal";
import { i18nProvider } from "@trumpf/ux.i18n";

defineAuthImage();

@Component({
    tag: "ux-w-my-machines-item",
    styleUrl: "my-machines-item.scss"
})
export class MyMachinesItem implements ComponentInterface {
    private i18nSubscriberId: string;

    @Element() domNode: HTMLUxWMyMachinesItemElement;

    /** name of the machine  */
    @Prop() machineName: string = "";

    /** custom name of the machine  */
    @Prop() customName: string = "";

    /** id of the machine */
    @Prop() machineId: string = "";

    /** url for machine image */
    @Prop() machineImageUrl: string = "";

    /** shop links */
    @Prop() shopLinks: ShopLink[] = [];

    /** link to detail page of machine */
    @Prop() detailLink: string = "";

    /** Link to create a new service call via a modal */
    @Prop() serviceCallModalLink: string = "";

    /** Title for Link to create a new service call */
    @Prop() serviceCallLinkText: string = "";

    /** Title for Link to detail page */
    @Prop() detailLinkText: string = "";

    /** text which is displayed on the overlay */
    @Prop() chooseShopTitle: string = "";

    /** is user not allowed to create service cases */
    @Prop() serviceCaseCreateDisabled: boolean;

    /** service case creation disallowed text */
    @Prop() serviceCaseCreateDisabledHeaderText?: string;

    /** service case creation disallowed text */
    @Prop() serviceCaseCreateDisabledBodyText?: string;

    /** data for the create-case micro frontend */
    @Prop() serviceCaseMicroFrontend?: ServiceCaseCreateConfig;

    /** indicates that ux-auth-image should be used */
    @Prop({ reflect: true }) authImage: boolean;

    /** indicates whether machine order tracking is active */
    @Prop() orderTracking: boolean = false;

    @State() overlayActive: boolean = false;

    connectedCallback() {
        this.i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this));
    }

    disconnectedCallback() {
        i18nProvider.unsubscribe(this.i18nSubscriberId);
    }

    render() {
        const { t } = i18nProvider;
        const {
            serviceCallModalLink,
            serviceCaseCreateDisabled,
            serviceCaseCreateDisabledHeaderText,
            serviceCaseCreateDisabledBodyText,
            serviceCaseMicroFrontend
        } = this;
        const modalName = `modal${this.machineId}`;
        const useServiceCaseMicroFrontend = !!serviceCaseMicroFrontend;

        const tileBodyStyles = this.authImage
            ? {
                  position: `relative`
              }
            : {
                  backgroundImage: `url(${this.machineImageUrl})`
              };

        return (
            <Host
                class={`ux-tile ${this.orderTracking ? "order-tracking" : ""}`}
                data-testid={`machine-${this.machineId}`}
            >
                <div class="ux-tile-head">
                    {this.orderTracking && (
                        <div class="order-tracking-bar ux-text-tiny ux-text-tiny--bold ux-u-text-uppercase">
                            {t("orderTracking.label")}
                        </div>
                    )}
                    <div>
                        <div
                            class="ux-tile-head__title"
                            data-testid={
                                this.customName
                                    ? "machine-custom-name"
                                    : "machine-name"
                            }
                        >
                            {!this.customName
                                ? this.machineName
                                : this.customName}
                        </div>
                        {this.customName && (
                            <div
                                class="ux-tile-head__machine-name ux-text-normal"
                                data-testid="machine-name"
                            >
                                {`(${this.machineName})`}
                            </div>
                        )}
                        <div
                            class="ux-tile-head__subtitle"
                            data-testid="machine-number"
                        >
                            {this.machineId}
                        </div>
                    </div>
                </div>
                <div class="ux-tile-body" style={tileBodyStyles}>
                    {this.authImage && (
                        <uxc-auth-image
                            src={this.machineImageUrl}
                            scope="mytrumpf"
                        >
                            <ux-image
                                width={305}
                                height={180}
                                alt={this.machineName}
                            ></ux-image>
                        </uxc-auth-image>
                    )}

                    <div class="ux-tile-body__button-group">
                        {serviceCallModalLink !== "" &&
                            !serviceCaseCreateDisabled &&
                            !useServiceCaseMicroFrontend && (
                                <ux-iws-async-modal
                                    src={serviceCallModalLink}
                                    name={modalName}
                                ></ux-iws-async-modal>
                            )}
                        {useServiceCaseMicroFrontend &&
                            !serviceCaseCreateDisabled && (
                                <ServiceCaseCreateModal
                                    modalName={modalName}
                                    env={serviceCaseMicroFrontend.env}
                                    prefillData={{
                                        productData: {
                                            assetId: this.machineId
                                        }
                                    }}
                                />
                            )}
                        {serviceCaseCreateDisabled && (
                            <ux-tooltip
                                tooltipTarget="ux-button[name='open-service-case-create-btn']"
                                align="left"
                            >
                                <div class="service-case-error-tooltip">
                                    <ux-icon
                                        class="ux-color-error"
                                        name="close"
                                        size-l="20"
                                    ></ux-icon>
                                    <p
                                        class="ux-text-title ux-text-title--bold headerText"
                                        data-testid="create-service-case-disabled"
                                    >
                                        {serviceCaseCreateDisabledHeaderText}
                                    </p>
                                    <p class="ux-text-normal bodyText">
                                        {serviceCaseCreateDisabledBodyText}
                                    </p>
                                </div>
                            </ux-tooltip>
                        )}
                        {(serviceCallModalLink !== "" ||
                            useServiceCaseMicroFrontend) && (
                            <ux-button
                                class="ux-w-my-machines-item__button"
                                size={"icon" as ButtonSize}
                                name="open-service-case-create-btn"
                                disabled={serviceCaseCreateDisabled}
                                data-trigger-async-modal={
                                    serviceCallModalLink !== "" &&
                                    !serviceCaseCreateDisabled &&
                                    !useServiceCaseMicroFrontend
                                        ? `open,${modalName}`
                                        : undefined
                                }
                                data-trigger-modal={
                                    !serviceCaseCreateDisabled &&
                                    useServiceCaseMicroFrontend
                                        ? `open,${modalName}`
                                        : undefined
                                }
                                type="button"
                                data-testid="create-service-case"
                            >
                                <ux-icon
                                    slot="icon"
                                    name="hand-plus-right"
                                    size-l="20"
                                />
                            </ux-button>
                        )}
                        {this.shopLinks.length === 1 && (
                            <ux-button
                                class="ux-w-my-machines-item__button"
                                size={"icon" as ButtonSize}
                                href={this.shopLinks[0].link}
                                title={this.shopLinks[0].linkText}
                                data-testid="shop-link"
                            >
                                <ux-icon slot="icon" name="cart" size-l="20" />
                            </ux-button>
                        )}
                        {this.shopLinks.length > 1 && (
                            <ux-button
                                class="ux-w-my-machines-item__button"
                                size={"icon" as ButtonSize}
                                onClick={() => {
                                    this.overlayActive = true;
                                }}
                                type="button"
                                data-testid="shop-button"
                            >
                                <ux-icon slot="icon" name="cart" size-l="20" />
                            </ux-button>
                        )}
                        <ux-button
                            class="ux-w-my-machines-item__button"
                            size={"icon" as ButtonSize}
                            href={this.detailLink}
                            title={this.detailLinkText}
                            data-testid="detail-link"
                        >
                            {" "}
                            <ux-icon
                                slot="icon"
                                name="chevron-right"
                                size-l="20"
                            />
                        </ux-button>
                    </div>
                    <div
                        class={{
                            "ux-tile-body__overlay": true,
                            "is-visible": this.overlayActive
                        }}
                    >
                        <div
                            class="ux-tile-body__overlay-close"
                            onClick={() => {
                                this.overlayActive = false;
                            }}
                        >
                            <ux-icon name="close"></ux-icon>
                        </div>
                        <div
                            class="ux-tile-body__overlay-content"
                            data-testid="shop-overlay"
                        >
                            <div>{this.chooseShopTitle}</div>
                            {this.shopLinks.map((shopLink) => (
                                <ux-button
                                    href={shopLink.link}
                                    title={shopLink.linkText}
                                    target={shopLink.linkTarget}
                                >
                                    {shopLink.linkText}{" "}
                                    <ux-icon
                                        name={getShopIcon(shopLink.shopType)}
                                    />
                                </ux-button>
                            ))}
                        </div>
                    </div>
                </div>
            </Host>
        );
    }
}

const getShopIcon = (type: ShopType) => {
    switch (type) {
        case ShopType.BENDING:
            return "cart-bending-tools";
        case ShopType.PUNCHING:
            return "cart-punching-tools";
        case ShopType.SPAREPARTS:
            return "cart-genuine-parts";
        default:
            return "cart";
    }
};
